import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for managing form drafts with auto-save to localStorage
 * @param {string} formKey - Unique identifier for the form
 * @param {Object} defaultData - Default form data
 * @param {Object} options - Configuration options
 * @returns {Object} - { draft, updateDraft, clearDraft, isDirty, lastSaved }
 */
export const useFormDraft = (
  formKey,
  defaultData = {},
  options = {}
) => {
  const {
    autoSave = true,
    saveDelay = 500,
    maxSize = 1024 * 1024, // 1MB
    persistOnUnload = true
  } = options;

  const [draft, setDraft] = useState(() => {
    try {
      const saved = localStorage.getItem(`draft:${formKey}`);
      return saved ? JSON.parse(saved) : defaultData;
    } catch (error) {
      console.error(`Error loading draft for ${formKey}:`, error);
      return defaultData;
    }
  });

  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const saveTimeoutRef = useRef(null);

  // Auto-save to localStorage
  useEffect(() => {
    if (!autoSave || !isDirty) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const draftString = JSON.stringify(draft);

        // Check size limit
        if (draftString.length > maxSize) {
          throw new Error(`Draft too large (${draftString.length} bytes). Maximum allowed: ${maxSize} bytes`);
        }

        localStorage.setItem(`draft:${formKey}`, draftString);
        setLastSaved(new Date());
        setIsDirty(false);
        setError(null);

        console.log(`💾 Draft saved for ${formKey}`);
      } catch (err) {
        console.error(`Error saving draft for ${formKey}:`, err);
        setError(err.message);
      }
    }, saveDelay);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [draft, formKey, autoSave, saveDelay, isDirty, maxSize]);

  // Load draft on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(`draft:${formKey}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        setDraft(parsed);
        console.log(`📂 Draft loaded for ${formKey}`);
      }
    } catch (err) {
      console.error(`Error loading draft for ${formKey}:`, err);
      setError(err.message);
    }
  }, [formKey]);

  // Save on page unload
  useEffect(() => {
    if (!persistOnUnload) return;

    const handleBeforeUnload = () => {
      if (isDirty) {
        try {
          const draftString = JSON.stringify(draft);
          localStorage.setItem(`draft:${formKey}`, draftString);
          console.log(`💾 Draft saved on unload for ${formKey}`);
        } catch (err) {
          console.error(`Error saving draft on unload for ${formKey}:`, err);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [draft, formKey, persistOnUnload, isDirty]);

  // Listen for offline events to force save
  useEffect(() => {
    const handleOffline = () => {
      if (isDirty) {
        try {
          const draftString = JSON.stringify(draft);
          localStorage.setItem(`draft:${formKey}`, draftString);
          console.log(`💾 Draft saved on offline for ${formKey}`);
          setLastSaved(new Date());
          setIsDirty(false);
        } catch (err) {
          console.error(`Error saving draft on offline for ${formKey}:`, err);
        }
      }
    };

    window.addEventListener('app:offline', handleOffline);
    return () => window.removeEventListener('app:offline', handleOffline);
  }, [draft, formKey, isDirty]);

  // Clear draft
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(`draft:${formKey}`);
      setDraft(defaultData);
      setIsDirty(false);
      setLastSaved(null);
      setError(null);
      console.log(`🗑️ Draft cleared for ${formKey}`);
    } catch (err) {
      console.error(`Error clearing draft for ${formKey}:`, err);
      setError(err.message);
    }
  }, [formKey, defaultData]);

  // Update draft
  const updateDraft = useCallback((updates) => {
    setDraft(prev => {
      const newDraft = { ...prev, ...updates };
      return newDraft;
    });
  }, []);

  // Manual save
  const saveDraft = useCallback(() => {
    try {
      const draftString = JSON.stringify(draft);
      if (draftString.length > maxSize) {
        throw new Error(`Draft too large (${draftString.length} bytes)`);
      }
      localStorage.setItem(`draft:${formKey}`, draftString);
      setLastSaved(new Date());
      setIsDirty(false);
      setError(null);
      console.log(`💾 Draft manually saved for ${formKey}`);
      return true;
    } catch (err) {
      console.error(`Error manually saving draft for ${formKey}:`, err);
      setError(err.message);
      return false;
    }
  }, [draft, formKey, maxSize]);

  // Get draft age
  const getDraftAge = useCallback(() => {
    const saved = localStorage.getItem(`draft:${formKey}`);
    if (!saved) return null;

    try {
      const parsed = JSON.parse(saved);
      const timestamp = parsed._timestamp;
      if (!timestamp) return null;

      const age = Date.now() - timestamp;
      return age;
    } catch {
      return null;
    }
  }, [formKey]);

  // Add timestamp to draft
  useEffect(() => {
    if (isDirty) {
      setDraft(prev => ({
        ...prev,
        _timestamp: Date.now()
      }));
    }
  }, [draft, isDirty]);

  return {
    draft,
    updateDraft,
    clearDraft,
    saveDraft,
    isDirty,
    lastSaved,
    error,
    getDraftAge
  };
};

export default useFormDraft;
